import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Alert,
  Button,
  Snackbar,
  Stack,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { api } from './ApiContext';
import { useAuth } from './AuthContext';
import { ToastCodeCaption } from '../components/ToastCodeCaption';
import { nextClientToastCode } from '../utils/toastCodes';
import { APP_TOP_TOAST_SNACKBAR_SX } from '../utils/appSnackbarSx';
import {
  ACTIVE_BACKGROUND_JOB_KEY,
  AFTER_RULE_SYNC_RELOAD_KEY,
  AFTER_TAG_WIPE_COMPLETION_KEY,
  buildRuleSyncCompletionToastFromJob,
  buildSupplier2bProposalCompletionToastFromJob,
  buildTagWipeCompletionToastFromAggregate,
  coalesceTagWipeAggregatePayload,
  completionDiagnosticsFromJobPoll,
  friendlyBackgroundJobProgressMessage,
  humanizeBackgroundJobFailure,
  type RuleSyncCompletionToastPayload,
  type RuleSyncUiMode
} from '../utils/ruleSyncJobToasts';

const HEAVY_RULE_JOB_TIMEOUT_MS = 120_000;
const POLL_MAX_TRANSIENT_FAILURES = 8;
export const BG_JOB_COMPLETED_EVENT = 'ff-background-job-completed';

/** Outlined actions on filled progress toasts (inherit text on colored Alert). */
const progressToastActionSx = {
  borderColor: 'rgba(255,255,255,0.7)',
  bgcolor: 'rgba(255,255,255,0.12)',
  fontWeight: 600,
  '&:hover': {
    borderColor: 'rgba(255,255,255,0.95)',
    bgcolor: 'rgba(255,255,255,0.22)',
  },
  '&.Mui-disabled': {
    borderColor: 'rgba(255,255,255,0.35)',
    color: 'rgba(255,255,255,0.55)',
  },
} as const;

type JobKind = 'rule_sync' | 'tag_wipe' | string;

type TrackedJob = {
  jobId: string;
  jobKind: JobKind;
  syncMode?: RuleSyncUiMode | null;
};

type CompletionNotice = RuleSyncCompletionToastPayload & {
  severity: 'success' | 'warning' | 'error' | 'info';
  jobKind: JobKind;
};

export type BackgroundJobNotice = CompletionNotice;

type BackgroundJobsContextValue = {
  activeJobId: string | null;
  activeJobKind: JobKind | null;
  /** Short copy for snackbar / notifications panel. */
  progressMessage: string | null;
  /** Raw server progress (stage / user / batch) for the Tx Review top banner. */
  detailedProgressMessage: string | null;
  cancelRequested: boolean;
  isRunning: boolean;
  /** Most recent completed/cancelled/failed job toast (stays until dismissed). */
  latestNotice: BackgroundJobNotice | null;
  trackJob: (jobId: string, jobKind?: JobKind, syncMode?: RuleSyncUiMode | null) => void;
  requestCancel: () => Promise<void>;
  clearCompletionNotice: () => void;
};

const BackgroundJobsContext = createContext<BackgroundJobsContextValue | null>(null);

function readStoredJob(): TrackedJob | null {
  try {
    const raw = sessionStorage.getItem(ACTIVE_BACKGROUND_JOB_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TrackedJob;
    const jobId = String(parsed?.jobId || '').trim();
    if (!jobId) return null;
    const syncMode =
      parsed?.syncMode === 'full' || parsed?.syncMode === 'gaps' ? parsed.syncMode : null;
    return { jobId, jobKind: String(parsed?.jobKind || 'rule_sync'), syncMode };
  } catch {
    return null;
  }
}

function writeStoredJob(job: TrackedJob | null) {
  try {
    if (!job) sessionStorage.removeItem(ACTIVE_BACKGROUND_JOB_KEY);
    else sessionStorage.setItem(ACTIVE_BACKGROUND_JOB_KEY, JSON.stringify(job));
  } catch {
    // ignore quota / private mode
  }
}

export function BackgroundJobsProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [tracked, setTracked] = useState<TrackedJob | null>(() => readStoredJob());
  const [progressMessage, setProgressMessage] = useState<string | null>(null);
  const [detailedProgressMessage, setDetailedProgressMessage] = useState<string | null>(null);
  const [cancelRequested, setCancelRequested] = useState(false);
  const [completion, setCompletion] = useState<CompletionNotice | null>(null);
  /** User closed the in-progress toast; job keeps running (see notifications / Tx Review). */
  const [progressToastDismissed, setProgressToastDismissed] = useState(false);
  const pollGenRef = useRef(0);

  const trackJob = useCallback(
    (jobId: string, jobKind: JobKind = 'rule_sync', syncMode?: RuleSyncUiMode | null) => {
      const id = String(jobId || '').trim();
      if (!id) return;
      const next: TrackedJob = { jobId: id, jobKind, syncMode: syncMode ?? null };
      writeStoredJob(next);
      setTracked(next);
      setCancelRequested(false);
      setCompletion(null);
      setProgressToastDismissed(false);
      setProgressMessage(
        friendlyBackgroundJobProgressMessage({
          jobKind,
          syncMode: syncMode ?? null,
          progressMessage:
            jobKind === 'tag_wipe'
              ? 'Removing classification tags 0%'
              : jobKind === 'supplier_2b_proposals'
                ? 'Step 2B non-merchant proposals: 0% · 0 proposed rules created'
                : null
        })
      );
      setDetailedProgressMessage(
        jobKind === 'tag_wipe'
          ? 'Removing classification tags 0%'
          : jobKind === 'supplier_2b_proposals'
            ? 'Step 2B non-merchant proposals: 0% · 0 proposed rules created'
            : syncMode === 'gaps'
              ? 'Cashflow 0% · Overall 0%'
              : 'Cashflow 0% · Overall 0%'
      );
    },
    []
  );

  const clearTracked = useCallback(() => {
    writeStoredJob(null);
    setTracked(null);
    setCancelRequested(false);
    setProgressMessage(null);
    setDetailedProgressMessage(null);
    setProgressToastDismissed(false);
  }, []);

  const requestCancel = useCallback(async () => {
    const id = tracked?.jobId;
    if (!id || cancelRequested) return;
    setCancelRequested(true);
    setProgressMessage(
      friendlyBackgroundJobProgressMessage({
        jobKind: tracked?.jobKind,
        syncMode: tracked?.syncMode,
        cancelRequested: true
      })
    );
    setDetailedProgressMessage((prev) =>
      prev ? `${prev} (cancel requested…)` : 'Cancel requested… waiting for server to stop between batches.'
    );
    try {
      await api.post(`/api/transaction-classification-rules/rule-sync-jobs/${id}/cancel`);
    } catch {
      // poll will surface terminal status / orphan reconcile message
    }
  }, [tracked?.jobId, tracked?.jobKind, tracked?.syncMode, cancelRequested]);

  // Resume any active server job after login / hard refresh.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/transaction-classification-rules/rule-sync-jobs/active');
        if (cancelled) return;
        const job = res.data?.job;
        const jobId = String(job?.job_id || '').trim();
        if (!jobId) return;
        const jobKind = String(job?.job_kind || 'rule_sync');
        const step = job?.current_step != null ? String(job.current_step) : '';
        const syncMode: RuleSyncUiMode | null =
          jobKind === 'tag_wipe' || jobKind === 'supplier_2b_proposals'
            ? null
            : step.includes('address_missing')
              ? 'gaps'
              : 'full';
        trackJob(jobId, jobKind, syncMode);
        const rawPm = job?.progress_message != null ? String(job.progress_message).trim() : '';
        setProgressMessage(
          friendlyBackgroundJobProgressMessage({
            jobKind,
            syncMode,
            currentStep: step || null,
            progressMessage: rawPm || null
          })
        );
        if (rawPm) setDetailedProgressMessage(rawPm);
      } catch {
        // ignore — local storage job id may still poll
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id, trackJob]);

  // App-wide poll — survives Tx Review unmount / route changes.
  useEffect(() => {
    if (!tracked?.jobId || !isAuthenticated) return;
    const jobId = tracked.jobId;
    let cancelled = false;
    let timer: number | null = null;
    let transientFailures = 0;
    const gen = ++pollGenRef.current;

    const finishWithNotice = (notice: CompletionNotice, opts?: { reloadTxReview?: boolean }) => {
      if (pollGenRef.current !== gen) return;
      clearTracked();
      // Only persist successful completions for post-reload toasts. Failures must not be
      // re-shown as green success banners when the user later opens Tx Review.
      if (notice.severity === 'success') {
        try {
          if (notice.jobKind === 'tag_wipe') {
            sessionStorage.setItem(AFTER_TAG_WIPE_COMPLETION_KEY, JSON.stringify(notice).slice(0, 12_000));
          } else if (notice.jobKind === 'rule_sync') {
            sessionStorage.setItem(AFTER_RULE_SYNC_RELOAD_KEY, JSON.stringify(notice).slice(0, 12_000));
          }
        } catch {
          // ignore
        }
      } else {
        try {
          if (notice.jobKind === 'tag_wipe') sessionStorage.removeItem(AFTER_TAG_WIPE_COMPLETION_KEY);
          if (notice.jobKind === 'rule_sync') sessionStorage.removeItem(AFTER_RULE_SYNC_RELOAD_KEY);
        } catch {
          // ignore
        }
      }
      setCompletion(notice);
      // Tx Review listens for this and soft-refreshes the table (fetchData). Prefer that over
      // window.location.reload(), which races toast persistence and can leave stale rows if the
      // reload is skipped or delayed.
      window.dispatchEvent(
        new CustomEvent(BG_JOB_COMPLETED_EVENT, {
          detail: {
            jobKind: notice.jobKind,
            reloadTxReview: Boolean(opts?.reloadTxReview),
            notice
          }
        })
      );
    };

    const poll = async () => {
      try {
        const res = await api.get(`/api/transaction-classification-rules/rule-sync-jobs/${jobId}`, {
          timeout: HEAVY_RULE_JOB_TIMEOUT_MS
        });
        if (cancelled || pollGenRef.current !== gen) return;
        transientFailures = 0;
        const job = (res.data?.job || {}) as Record<string, unknown>;
        const status = String(job.status || '').trim().toLowerCase();
        const jobKind = String(job.job_kind || tracked.jobKind || 'rule_sync');
        const step = job.current_step != null ? String(job.current_step) : null;
        const syncMode: RuleSyncUiMode | null =
          tracked.syncMode ||
          (jobKind === 'tag_wipe' || jobKind === 'supplier_2b_proposals'
            ? null
            : step && step.includes('address_missing')
              ? 'gaps'
              : 'full');
        const userNotice = job.user_notice != null ? String(job.user_notice).trim() : '';
        const rawPm = job.progress_message != null ? String(job.progress_message).trim() : '';
        const progressForUi = rawPm || userNotice || null;
        setProgressMessage(
          friendlyBackgroundJobProgressMessage({
            jobKind,
            syncMode,
            currentStep: step,
            progressMessage: progressForUi,
            cancelRequested
          })
        );
        if (progressForUi) {
          setDetailedProgressMessage(
            cancelRequested ? `${progressForUi} (cancel requested…)` : progressForUi
          );
        }
        const { requestId, jobId: jid } = completionDiagnosticsFromJobPoll(res, jobId, job);

        if (status === 'completed') {
          if (jobKind === 'tag_wipe') {
            const jobResult = job.result as Record<string, unknown> | undefined;
            const agg = coalesceTagWipeAggregatePayload(jobResult) ?? jobResult;
            finishWithNotice(
              {
                ...buildTagWipeCompletionToastFromAggregate(
                  String(job.message || 'Tag wipe completed.'),
                  agg
                ),
                requestId,
                jobId: jid,
                severity: 'success',
                jobKind
              },
              { reloadTxReview: true }
            );
            return;
          }
          if (jobKind === 'supplier_2b_proposals') {
            finishWithNotice(
              {
                ...buildSupplier2bProposalCompletionToastFromJob(job),
                requestId,
                jobId: jid,
                severity: 'success',
                jobKind
              },
              { reloadTxReview: false }
            );
            return;
          }
          finishWithNotice(
            {
              ...buildRuleSyncCompletionToastFromJob(job),
              requestId,
              jobId: jid,
              severity: 'success',
              jobKind
            },
            { reloadTxReview: true }
          );
          return;
        }
        if (status === 'cancelled') {
          finishWithNotice({
            title: String(
              job.message ||
                (jobKind === 'tag_wipe'
                  ? 'Tag wipe cancelled; completed batches were kept on the server.'
                  : jobKind === 'supplier_2b_proposals'
                    ? 'Step 2B proposal generation cancelled. Completed pages were kept on the server.'
                    : 'Rule sync cancelled. Completed work was kept; remaining steps were not run.')
            ),
            segments: [],
            requestId,
            jobId: jid,
            severity: 'warning',
            jobKind
          });
          return;
        }
        if (status === 'failed') {
          const restartedAs = String(job.restarted_as_job_id || '').trim();
          if (restartedAs && jobKind === 'rule_sync') {
            // Deploy/worker recycle: server finalized this job and started a successor.
            const notice =
              String(job.user_notice || job.message || '').trim() ||
              'Rule sync was interrupted by a server restart and has been restarted automatically.';
            // trackJob clears completion — set the info toast after switching to the successor.
            trackJob(restartedAs, 'rule_sync', syncMode);
            setCompletion({
              title: notice,
              segments: ['Tracking the restarted server job…'],
              requestId,
              jobId: jid,
              severity: 'info',
              jobKind
            });
            return;
          }
          finishWithNotice({
            title: humanizeBackgroundJobFailure(jobKind, job.error || job.message),
            segments: [],
            requestId,
            jobId: jid,
            severity: 'error',
            jobKind
          });
          return;
        }
      } catch (err: any) {
        if (cancelled || pollGenRef.current !== gen) return;
        const status = err?.response?.status;
        const transient =
          !status || status === 502 || status === 503 || status === 504 || err?.code === 'ECONNABORTED';
        if (transient && transientFailures + 1 < POLL_MAX_TRANSIENT_FAILURES) {
          transientFailures += 1;
          const waitMsg = `Waiting for job status… temporary server issue (retry ${transientFailures}/${POLL_MAX_TRANSIENT_FAILURES}).`;
          setProgressMessage(waitMsg);
          setDetailedProgressMessage(waitMsg);
          timer = window.setTimeout(() => void poll(), Math.min(12_000, 800 * transientFailures));
          return;
        }
        finishWithNotice({
          title:
            err?.response?.data?.error ||
            err?.message ||
            'Could not fetch background job status after repeated failures.',
          segments: [],
          severity: 'error',
          jobKind: tracked.jobKind,
          jobId
        });
        return;
      }
      timer = window.setTimeout(() => void poll(), cancelRequested ? 350 : 1200);
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
    };
  }, [tracked?.jobId, tracked?.jobKind, isAuthenticated, cancelRequested, clearTracked, trackJob]);

  const value = useMemo<BackgroundJobsContextValue>(
    () => ({
      activeJobId: tracked?.jobId || null,
      activeJobKind: tracked?.jobKind || null,
      progressMessage,
      detailedProgressMessage,
      cancelRequested,
      isRunning: Boolean(tracked?.jobId),
      latestNotice: completion,
      trackJob,
      requestCancel,
      clearCompletionNotice: () => setCompletion(null)
    }),
    [
      tracked,
      progressMessage,
      detailedProgressMessage,
      cancelRequested,
      completion,
      trackJob,
      requestCancel
    ]
  );

  const completionCode = completion
    ? String(completion.requestId || completion.jobId || nextClientToastCode())
    : '';

  return (
    <BackgroundJobsContext.Provider value={value}>
      {children}
      <Snackbar
        open={Boolean(tracked?.jobId && progressMessage && !progressToastDismissed)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={APP_TOP_TOAST_SNACKBAR_SX}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setProgressToastDismissed(true)}
          sx={{ width: '100%', maxWidth: 560, alignItems: 'flex-start' }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {progressMessage}
          </Typography>
          <ToastCodeCaption
            jobId={tracked?.jobId || undefined}
            copyBody={
              [
                progressMessage,
                detailedProgressMessage && detailedProgressMessage !== progressMessage
                  ? `Detail: ${detailedProgressMessage}`
                  : ''
              ]
                .filter(Boolean)
                .join('\n')
            }
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              sx={progressToastActionSx}
              onClick={() => {
                setCompletion(null);
                navigate('/transaction-classification-review');
              }}
            >
              View Progress
            </Button>
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              disabled={cancelRequested}
              sx={progressToastActionSx}
              onClick={() => void requestCancel()}
            >
              {cancelRequested ? 'Cancelling…' : 'Cancel Process'}
            </Button>
          </Stack>
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(completion)}
        // Errors/warnings stay until the user dismisses (X); success/info may auto-hide.
        autoHideDuration={
          completion?.severity === 'error' || completion?.severity === 'warning' ? null : 20_000
        }
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          if (
            (completion?.severity === 'error' || completion?.severity === 'warning') &&
            reason === 'timeout'
          ) {
            return;
          }
          setCompletion(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: { xs: 72, sm: 80 }, zIndex: (theme) => theme.zIndex.snackbar }}
      >
        <Alert
          severity={completion?.severity || 'info'}
          variant="filled"
          onClose={() => setCompletion(null)}
          sx={{ width: '100%', maxWidth: 720 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
            {completion?.title}
          </Typography>
          {completion?.segments?.length ? (
            <Stack spacing={0.35} sx={{ mt: 0.75 }}>
              {completion.segments.map((seg, idx) => (
                <Typography
                  key={`${idx}-${seg.slice(0, 24) || 'blank'}`}
                  variant="body2"
                  component="div"
                  sx={{
                    lineHeight: 1.4,
                    whiteSpace: 'pre-wrap',
                    ...(seg ? {} : { minHeight: '0.75em' })
                  }}
                >
                  {seg || '\u00a0'}
                </Typography>
              ))}
            </Stack>
          ) : null}
          {completion ? (
            <ToastCodeCaption
              code={completionCode || nextClientToastCode()}
              requestId={completion.requestId}
              jobId={completion.jobId}
              copyBody={[completion.title, ...(completion.segments || [])].join('\n')}
            />
          ) : null}
        </Alert>
      </Snackbar>
    </BackgroundJobsContext.Provider>
  );
}

export function useBackgroundJobs(): BackgroundJobsContextValue {
  const ctx = useContext(BackgroundJobsContext);
  if (!ctx) {
    throw new Error('useBackgroundJobs must be used within BackgroundJobsProvider');
  }
  return ctx;
}
